import React, { useEffect, useState } from "react";
import axios from "axios";
import { Spinner } from "react-bootstrap";
import {
  FaPrint,
  FaSearch,
  FaEye,
  FaChartPie,
  FaUsers,
  FaFilter,
} from "react-icons/fa";
import "../Styles/AdminPages.css";
import formatName from "../utils/formatName";

const sortByNewestAttempt = (results) =>
  [...results].sort((a, b) => {
    const parseDate = (value) => {
      if (!value) return 0;
      const timestamp = new Date(String(value).replace(" ", "T")).getTime();
      return Number.isFinite(timestamp) ? timestamp : 0;
    };

    const dateDifference = parseDate(b.created_at) - parseDate(a.created_at);
    if (dateDifference !== 0) return dateDifference;

    return Number(b.attempt || 0) - Number(a.attempt || 0);
  });

function ResultPage() {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [userRole, setUserRole] = useState("");
  const [intakes, setIntakes] = useState([]);
  const [selectedIntake, setSelectedIntake] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isIntakeLoading, setIsIntakeLoading] = useState(false);

  const itemsPerPage = 20;

  const brandBlue = "#263F86";
  const softBlue = "rgba(95, 112, 245, 0.15)";
  const borderColor = "#EEF0F4";

  useEffect(() => {
    const loggedInUser = JSON.parse(localStorage.getItem("user"));
    setUserRole(loggedInUser?.user_role || "learner");
  }, []);

  useEffect(() => {
    setIsLoading(true);

    axios
      .get(`${process.env.REACT_APP_API}/getresult`)
      .then((response) => {
        if (response.data.success) {
          const newestFirst = sortByNewestAttempt(response.data.data || []);
          setData(newestFirst);
          setFilteredData(newestFirst);
        }
      })
      .catch((error) => {
        console.error("Error fetching leaderboard data:", error);
      })
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    setIsIntakeLoading(true);

    axios
      .get(`${process.env.REACT_APP_API}/getIntake`)
      .then((response) => {
        if (response.data.success && response.data.data) {
          const uniqueIntakes = [
            ...new Set(
              response.data.data
                .map((item) => item.intake)
                .filter((intake) => intake && intake.trim() !== ""),
            ),
          ];
          setIntakes(uniqueIntakes);
        } else {
          setIntakes([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching intake data:", error);
        setIntakes([]);
      })
      .finally(() => setIsIntakeLoading(false));
  }, []);

  useEffect(() => {
    const filtered = data.filter((item) => {
      const username = item.username || "";
      const intake = item.intake || "";

      return (
        username.toLowerCase().includes(searchQuery.toLowerCase()) &&
        (selectedIntake === "" || intake === selectedIntake)
      );
    });

    setFilteredData(filtered);
    setCurrentPage(1);
  }, [searchQuery, selectedIntake, data]);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleViewAttempts = (
    assessmentId,
    userId,
    attempt,
    username,
    created_at,
  ) => {
    const params = new URLSearchParams({
      assessmentId,
      userId,
      attempt,
      username,
      created_at,
    });
    window.open(`/examresult?${params.toString()}`, "_blank", "noopener,noreferrer");
  };

  const handlePrint = () => {
    const table = document.getElementById("printableTable").cloneNode(true);

    table.querySelectorAll(".print-hide").forEach((el) => el.remove());

    const printContent = table.outerHTML;
    const printWindow = window.open("", "_blank");

    printWindow.document.write(`
    <html>
      <head>
        <title>Print Assessment Results</title>
        <style>
          @page {
            size: A4 portrait;
            margin: 12mm;
          }

          * {
            box-sizing: border-box;
          }

          body {
            font-family: 'Manrope', sans-serif;
            margin: 0;
            padding: 0;
            color: #1F2937;
          }

          .print-wrapper {
            width: 100%;
            max-width: 100%;
            overflow: hidden;
          }

          table {
            width: 100% !important;
            max-width: 100% !important;
            border-collapse: collapse;
            table-layout: fixed;
            margin-top: 18px;
          }

          th, td {
            border: 1px solid #ddd;
            padding: 8px 7px;
            text-align: center;
            font-size: 10px;
            line-height: 1.4;
            word-break: break-word;
            overflow-wrap: anywhere;
          }

          th {
            background-color: #F8FAFC;
            color: #334155;
            font-weight: 800;
            text-transform: uppercase;
          }

          th:nth-child(1),
          td:nth-child(1) {
            width: 40%;
            text-align: left;
          }

          th:nth-child(2),
          td:nth-child(2) {
            width: 15%;
          }

          th:nth-child(3),
          td:nth-child(3) {
            width: 14%;
          }

          th:nth-child(4),
          td:nth-child(4) {
            width: 12%;
          }

          th:nth-child(5),
          td:nth-child(5) {
            width: 19%;
          }

          .review-column {
            display: none !important;
          }

          .print-name {
            display: block !important;
            font-weight: 700;
            color: #111827;
            white-space: normal !important;
          }

          .header {
            text-align: center;
            margin-bottom: 16px;
          }

          .header h1 {
            font-size: 26px;
            margin: 0 0 8px;
          }

          .header h4 {
            font-size: 14px;
            margin: 0 0 14px;
          }

          .header p {
            font-size: 12px;
            color: #555;
            margin: 0;
          }
        </style>
      </head>

      <body>
        <div class="print-wrapper">
          <div class="header">
            <h1>TMIS</h1>
            <h4>Tourism Management Institute of Singapore</h4>
            <p>Assessment Results</p>
          </div>

          ${printContent}
        </div>
      </body>
    </html>
  `);

    printWindow.document.close();
    printWindow.print();
  };

  const getPercentage = (value) => {
    const percentage = Number(value || 0);
    return Math.max(0, Math.min(100, percentage));
  };

  const PercentageCircle = ({ value }) => {
    const percentage = getPercentage(value);
    const color = percentage >= 70 ? "#166534" : "#B91C1C";

    return (
      <div
        style={{
          width: "42px",
          height: "42px",
          borderRadius: "50%",
          background: `conic-gradient(${color} ${
            percentage * 3.6
          }deg, #E8EEF6 0deg)`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto",
        }}
      >
        <div
          style={{
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            background: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "10px",
            fontWeight: 800,
            color,
          }}
        >
          {percentage.toFixed(0)}%
        </div>
      </div>
    );
  };

  return (
    <section className="admin-page" aria-labelledby="results-page-title">
      <div className="admin-page__header">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div>
            <div style={{ textAlign: "left" }}>
              <p className="admin-page__eyebrow">ASSESSMENT INSIGHTS</p>
              <h1 id="results-page-title">Results</h1>
              <p className="admin-page__intro">
                Review assessment performance, scores, intakes, and candidate
                progress.
              </p>
            </div>
          </div>

          <div className="admin-page__metric"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              background: "#263F86",
              padding: "10px 14px",
              borderRadius: "14px",
              boxShadow: "0 6px 18px rgba(38, 63, 134, 0.15)",
              minWidth: "170px",
              height: "64px",
            }}
          >
            <div
              style={{
                width: "38px",
                height: "38px",
                borderRadius: "12px",
                background: "rgba(255,255,255,0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#ffffff",
                flexShrink: 0,
              }}
            >
              <FaUsers size={15} />
            </div>

            <div style={{ lineHeight: 1 }}>
              <div
                style={{
                  fontSize: "10px",
                  fontWeight: 700,
                  color: "rgba(255,255,255,0.7)",
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                  marginBottom: "5px",
                }}
              >
                Total Candidates
              </div>

              <div
                style={{
                  fontSize: "28px",
                  fontWeight: 800,
                  color: "#ffffff",
                  textAlign: "right",
                }}
              >
                {filteredData.length}
              </div>
            </div>
          </div>
        </div>
      </div>

      {userRole === "admin" && (
        <div
          className="mb-4"
          style={{
            background: "#ffffff",
            borderRadius: "18px",
            padding: "18px 20px",
            boxShadow: "0 8px 24px rgba(0,0,0,0.04)",
            border: `1px solid ${borderColor}`,
          }}
        >
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
            <div className="d-flex align-items-center gap-2">
              <FaFilter color={brandBlue} />
              <h6 className="fw-bold mb-0">Filter Results</h6>
            </div>
          </div>

          <div className="row g-3">
            <div className="col-12 col-md-8">
              <div style={{ position: "relative" }}>
                <FaSearch
                  size={14}
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "14px",
                    transform: "translateY(-50%)",
                    color: "#5F6B7A",
                  }}
                />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by name"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{
                    borderRadius: "12px",
                    padding: "11px 14px 11px 38px",
                    border: `1px solid ${borderColor}`,
                    fontSize: "14px",
                  }}
                />
              </div>
            </div>

            <div className="col-12 col-md-4">
              <select
                className="form-select"
                value={selectedIntake}
                onChange={(e) => setSelectedIntake(e.target.value)}
                disabled={isIntakeLoading}
                style={{
                  borderRadius: "12px",
                  padding: "11px 14px",
                  border: `1px solid ${borderColor}`,
                  fontSize: "14px",
                }}
              >
                <option value="">All Intakes</option>
                {intakes.map((intake, index) => (
                  <option key={index} value={intake}>
                    {intake}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      <div
        style={{
          background: "#ffffff",
          borderRadius: "18px",
          padding: "20px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.04)",
          border: `1px solid ${borderColor}`,
        }}
      >
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
          <div className="d-flex align-items-center gap-2">
            <FaChartPie color={brandBlue} size={15} />

            <h6
              className="fw-bold mb-0"
              style={{
                color: "#1F2937",
                fontSize: "15px",
              }}
            >
              Assessment Result List
            </h6>
          </div>

          {userRole === "admin" && (
            <button
              className="btn d-flex align-items-center"
              onClick={handlePrint}
              disabled={isLoading}
              style={{
                background: brandBlue,
                color: "#ffffff",
                border: "none",
                borderRadius: "10px",
                padding: "7px 12px",
                fontWeight: 700,
                fontSize: "13px",
                boxShadow: "0 6px 16px rgba(38,63,134,0.18)",
                lineHeight: 1,
              }}
            >
              <FaPrint size={12} className="me-2" />
              Print Results
            </button>
          )}
        </div>

        {isLoading ? (
          <div
            className="d-flex flex-column justify-content-center align-items-center"
            style={{ height: "50vh" }}
          >
            <Spinner animation="border" style={{ color: brandBlue }} />
            <p className="mt-3 mb-0">Loading results...</p>
          </div>
        ) : (
          userRole === "admin" && (
            <div className="table-responsive">
              <table
                id="printableTable"
                className="table mb-0"
                style={{
                  borderCollapse: "collapse",
                  width: "100%",
                  fontSize: "14px",
                }}
              >
                <thead>
                  <tr>
                    <th style={thStyleLeft}>Name</th>
                    <th style={thStyle}>Percentage</th>
                    <th style={thStyle}>Answered</th>
                    <th style={thStyle}>Scores</th>
                    <th style={thStyle}>Intake</th>
                    <th className="review-column" style={thStyle}>
                      Action
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {currentData.length > 0 ? (
                    currentData.map((item, index) => {
                      const percentage = getPercentage(item.percentage);
                      const passed = percentage >= 70;

                      return (
                        <tr
                          key={index}
                          style={{
                            borderBottom: `1px solid ${borderColor}`,
                          }}
                        >
                          <td style={tdStyleLeft}>
                            <div className="d-flex align-items-center gap-2">
                              <div
                                className="print-hide"
                                style={{
                                  width: "32px",
                                  height: "32px",
                                  borderRadius: "10px",
                                  background: softBlue,
                                  color: brandBlue,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  fontWeight: 800,
                                  fontSize: "13px",
                                  flexShrink: 0,
                                }}
                              >
                                {(item.username || "U").charAt(0).toUpperCase()}
                              </div>

                              <span
                                className="print-name"
                                style={{
                                  fontWeight: 700,
                                  color: "#1F2937",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {formatName(item.username)}
                              </span>
                            </div>
                          </td>

                          <td style={tdStyle}>
                            <PercentageCircle value={item.percentage} />
                          </td>

                          <td style={tdStyle}>{item.total_questions}</td>

                          <td
                            style={{
                              ...tdStyle,
                              fontWeight: 800,
                              color: passed ? "#15803D" : "#B91C1C",
                            }}
                          >
                            {item.total_correct_answers}
                          </td>

                          <td style={tdStyle}>{item.intake || "-"}</td>

                          <td className="review-column" style={tdStyle}>
                            <button
                              className="btn"
                              onClick={() =>
                                handleViewAttempts(
                                  item.assessment_id,
                                  item.user_id,
                                  item.attempt,
                                  item.username,
                                  item.created_at,
                                )
                              }
                              style={{
                                background: "#F8FAFC",
                                color: brandBlue,
                                border: `1px solid ${borderColor}`,
                                borderRadius: "9px",
                                padding: "7px 11px",
                                fontWeight: 800,
                                fontSize: "14px",
                              }}
                            >
                              <FaEye size={12} className="me-2" />
                              View
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td
                        colSpan="6"
                        className="text-center"
                        style={{
                          padding: "36px",
                          color: "#5F6B7A",
                          fontWeight: 600,
                        }}
                      >
                        No results found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )
        )}
      </div>

      {filteredData.length > itemsPerPage && (
        <nav aria-label="Page navigation" className="mt-4">
          <ul className="pagination justify-content-center flex-wrap gap-1">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                style={{ borderRadius: "10px" }}
              >
                Previous
              </button>
            </li>

            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(
                (page) =>
                  page === 1 ||
                  page === totalPages ||
                  (page >= currentPage - 2 && page <= currentPage + 2),
              )
              .map((page) => (
                <li
                  key={page}
                  className={`page-item ${
                    page === currentPage ? "active" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() => handlePageChange(page)}
                    style={{
                      borderRadius: "10px",
                      background: page === currentPage ? brandBlue : "#fff",
                      borderColor:
                        page === currentPage ? brandBlue : borderColor,
                    }}
                  >
                    {page}
                  </button>
                </li>
              ))}

            <li
              className={`page-item ${
                currentPage === totalPages ? "disabled" : ""
              }`}
            >
              <button
                className="page-link"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                style={{ borderRadius: "10px" }}
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
      )}
    </section>
  );
}

const thStyle = {
  textAlign: "center",
  background: "#F8FAFC",
  color: "#475569",
  fontSize: "12px",
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  borderBottom: "1px solid #EEF0F4",
  padding: "13px 14px",
  whiteSpace: "nowrap",
};

const thStyleLeft = {
  ...thStyle,
  textAlign: "left",
};

const tdStyle = {
  textAlign: "center",
  padding: "12px 14px",
  color: "#334155",
  fontWeight: 600,
  letterSpacing: "0.5px",
  verticalAlign: "middle",
  whiteSpace: "nowrap",
};

const tdStyleLeft = {
  ...tdStyle,
  textAlign: "left",
};

export default ResultPage;
